import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { ApiRequestError } from "@/api/client";
import type { ApiError } from "@/api/types";
import { signalSessionExpired } from "@/context/sessionExpiredSignal";
import { Button } from "./Button";
import { cn } from "@/lib/cn";

export interface ErrorStateProps {
  /** The thrown error (ApiRequestError carries status + request id). */
  error?: unknown;
  /** Override the title; defaults by status (403 -> forbidden, 401 -> session expired, 0 -> network). */
  title?: string;
  body?: string;
  onRetry?: () => void;
  /** For a 401: re-authenticate. Defaults to raising the app-wide session-expired overlay. */
  onSignIn?: () => void;
  className?: string;
}

function asApiError(error: unknown): ApiError | null {
  return error instanceof ApiRequestError ? error.error : null;
}

/**
 * Error state for any async surface. Distinguishes forbidden (403) and network (status 0) from a
 * generic failure, and surfaces the X-Request-ID so support can correlate. Never silently blank.
 */
export function ErrorState({ error, title, body, onRetry, onSignIn, className }: ErrorStateProps) {
  const { t } = useTranslation();
  const api = asApiError(error);
  const status = api?.status;
  const sessionExpired = status === 401;

  let resolvedTitle = title;
  let resolvedBody = body;
  if (!resolvedTitle) {
    if (status === 403) resolvedTitle = t("state.forbiddenTitle");
    else if (status === 404) resolvedTitle = t("state.notFoundTitle");
    else if (status === 0) resolvedTitle = t("state.networkError");
    else if (sessionExpired) resolvedTitle = t("auth.sessionExpiredTitle");
    else resolvedTitle = t("state.errorTitle");
  }
  if (!resolvedBody) {
    resolvedBody = sessionExpired
      ? t("auth.sessionExpiredBody")
      : status === 403
        ? t("state.forbiddenBody")
        : t("state.errorBody");
  }

  return (
    <div
      role="alert"
      className={cn(
        "flex flex-col items-center justify-center gap-3 px-6 py-14 text-center",
        className,
      )}
    >
      <span
        className="flex size-12 items-center justify-center rounded-full bg-(--danger-subtle) text-(--danger)"
        aria-hidden
      >
        <AlertTriangle className="size-6" />
      </span>
      <div className="flex max-w-sm flex-col gap-1">
        <p className="text-(length:--text-sm) font-semibold text-(--text-strong)">
          {resolvedTitle}
        </p>
        <p className="text-(length:--text-xs) leading-relaxed text-(--text-muted)">
          {resolvedBody}
        </p>
        {api?.requestId && (
          <p className="tabular-mono text-(length:--text-2xs) text-(--text-subtle)">
            {t("state.requestId", { id: api.requestId })}
          </p>
        )}
      </div>
      {sessionExpired ? (
        <Button
          size="sm"
          // userInitiated: a human pressed this, so the provider must show the overlay outright — it
          // must NOT be swallowed by the in-flight gate of a silent recovery (which would leave the
          // button visibly dead) and must NOT buy a refresh of its own (which, on success, just
          // re-renders this same error card). renewalFailure null for the same reason: no renewal
          // failed here, so there is no verdict to act on.
          onClick={
            onSignIn ??
            (() =>
              signalSessionExpired({
                tokenWasSent: false,
                renewalFailure: null,
                userInitiated: true,
              }))
          }
        >
          {t("auth.signIn")}
        </Button>
      ) : (
        onRetry &&
        status !== 403 && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            {t("action.retry")}
          </Button>
        )
      )}
    </div>
  );
}
